import { DOCUMENT } from '@angular/common';
import { Injectable, OnDestroy, inject, signal } from '@angular/core';

import { ELF_PREFIX } from 'src/app/app.constants';
import { NativeService } from 'src/app/services/native/native.service';

export type Appearance = 'auto' | 'day' | 'night';
export type DaylightPhase = 'dawn' | 'morning' | 'midday' | 'evening' | 'night';

export const APPEARANCE_STORAGE_KEY = ELF_PREFIX + 'appearance';
export const DAYLIGHT_PHASES: readonly DaylightPhase[] = [
  'dawn',
  'morning',
  'midday',
  'evening',
  'night',
];

export function resolveDaylightPhase(
  appearance: Appearance,
  hour = new Date().getHours(),
): DaylightPhase {
  if (appearance === 'day') {
    return 'midday';
  }

  if (appearance === 'night') {
    return 'night';
  }

  if (hour >= 5 && hour < 8) {
    return 'dawn';
  }

  if (hour >= 8 && hour < 12) {
    return 'morning';
  }

  if (hour >= 12 && hour < 16) {
    return 'midday';
  }

  if (hour >= 16 && hour < 20) {
    return 'evening';
  }

  return 'night';
}

@Injectable({ providedIn: 'root' })
export class AppearanceService implements OnDestroy {
  private document = inject(DOCUMENT);
  private native = inject(NativeService);
  private intervalId?: ReturnType<typeof setInterval>;
  private initialized = false;

  readonly preference = signal<Appearance>('night');
  readonly phase = signal<DaylightPhase>('night');

  init(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.preference.set(this.readStoredPreference());
    this.apply();

    this.intervalId = setInterval(() => this.apply(), 60_000);
    this.document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  setPreference(preference: Appearance): void {
    this.preference.set(preference);

    try {
      this.document.defaultView?.localStorage.setItem(APPEARANCE_STORAGE_KEY, preference);
    } catch {
      // The visual preference still applies for this session when storage is unavailable.
    }

    this.apply();
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }

    this.document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  private readonly handleVisibilityChange = (): void => {
    this.document.documentElement.classList.toggle('document-hidden', this.document.hidden);

    if (!this.document.hidden) {
      this.apply();
    }
  };

  private readStoredPreference(): Appearance {
    try {
      const stored = this.document.defaultView?.localStorage.getItem(APPEARANCE_STORAGE_KEY);
      return stored === 'auto' || stored === 'day' || stored === 'night' ? stored : 'night';
    } catch {
      return 'night';
    }
  }

  private apply(): void {
    const phase = resolveDaylightPhase(this.preference());
    const root = this.document.documentElement;

    for (const daylightPhase of DAYLIGHT_PHASES) {
      root.classList.toggle(daylightPhase, daylightPhase === phase);
    }

    root.dataset['appearance'] = this.preference();
    root.dataset['phase'] = phase;
    root.style.colorScheme = phase === 'night' ? 'dark' : 'light';
    this.phase.set(phase);

    const themeColors: Record<DaylightPhase, string> = {
      dawn: '#fbf6f2',
      morning: '#fbfaf7',
      midday: '#fcfcfb',
      evening: '#f7efe2',
      night: '#15141a',
    };
    this.document
      .querySelector<HTMLMetaElement>('meta[name="theme-color"]')
      ?.setAttribute('content', themeColors[phase]);
    this.native.syncStatusBar(phase);
  }
}
